import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Modal, Select, DatePicker, message, Row, Col } from 'antd';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import moment from 'moment';
import { Form } from 'formik-antd';
import { CloseOutlined } from '@ant-design/icons';
import { ButtonWrapper } from 'components/ButtonWrapper';
import { FormInput, CustomInput } from 'atoms';
import { addUser } from '../../store/pageSlice';
import { getClientList, getLocations } from '../../store/clientSlice';

const StyledModal = styled(Modal)`
	.ant-modal-content {
		width: 883px;
		padding: 26px 32px 28px 32px;
		background: #ffffff;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 24px;
		border-radius: 0;
		margin: 0 auto;
		position: relative;
		left: calc(50% - 883px / 2 + 123px);
		top: 70%;
		transform: translateY(-50%, %);
	}
	.ant-modal-header {
		border-bottom: none;
		padding: 0;
	}
	.ant-modal-body {
		padding: 0;
		width: 100%;
	}
	.ant-modal-title {
		font-family: Inter, sans-serif;
		font-style: normal;
		font-weight: 600;
		font-size: 24px;
		line-height: 29px;
		color: #000000;
		white-space: nowrap;
	}
	.ant-modal-footer {
		text-align: right !important;
		padding: 10px 0;
		display: flex !important;
		justify-content: flex-end !important;
		gap: 8px;
	}
`;

const ModalContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding: 0px;
	gap: 48px;
	width: 819px;
`;

const FormSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding: 0px;
	gap: 24px;
	width: 819px;
`;

const CancelButtonWrapper = styled(ButtonWrapper)`
	border: 1px solid #ff3b30;
	color: #ff3b30;
	background: #ffffff;
	&:hover {
		background: #ffffff;
		color: #ff3b30;
		border: 1px solid #ff3b30;
	}
`;

const CreateButtonWrapper = styled(ButtonWrapper)`
	background: #143e60;
	color: #ffffff;
	border: none;
	&:hover {
		background: #143e60;
		color: #ffffff;
	}
`;

const FormButtonRow = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 27px;
	width: 100%;
	margin-top: 32px;
`;

const fetchCityManagers = async (dispatch, setCityManagerOptions, setLoading) => {
	setLoading(true);
	try {
		const res = await dispatch(
			getClientList({
				filter_by: { user_type: 'city_manager' },
				search_type: 'employee',
				limit: 100,
			})
		);
		let cityManagerData = (res?.rows || res?.data || []).map((item) => ({
			value: item?.id,
			label: `${item?.first_name || ''} ${item?.last_name || ''}`.trim(),
		}));
		if (cityManagerData.length === 0) {
			cityManagerData = [{ value: '', label: 'No City Managers Found' }];
		}
		setCityManagerOptions(cityManagerData);
	} catch (e) {
		console.log('City Manager fetch error:', e);
		setCityManagerOptions([{ value: '', label: 'No City Managers Found' }]);
	} finally {
		setLoading(false);
	}
};

export default function CreateBusinessUnitModal({ open, onCancel, onCreate }) {
	const dispatch = useDispatch();
	const [submitting, setSubmitting] = useState(false);
	const [cityManagerOptions, setCityManagerOptions] = useState([]);
	const [selectedCityManager, setSelectedCityManager] = useState('');
	const [loading, setLoading] = useState(false);
	const buNameOptions = [];
	const [apiError, setApiError] = useState('');
	// const [buCodeError, setBuCodeError] = useState('');
	const resetFormRef = useRef(null);

	useEffect(() => {
		if (open) {
			fetchCityManagers(
				dispatch,
				(options) => {
					setCityManagerOptions(options);
					setSelectedCityManager(''); // Always clear selection when modal opens
				},
				setLoading
			);
			dispatch(getLocations({ search_type: 'location', filter_by: {} }));
		}
	}, [dispatch, open]);

	// Refactor handleSubmit to reduce cognitive complexity
	const formatAppointmentDate = (date) => {
		if (!date) return undefined;
		let d = date;
		// Handle Moment.js objects (from antd DatePicker)
		if (d._isAMomentObject && typeof d.format === 'function') {
			return d.format('YYYY-MM-DDTHH:mm:ss');
		}
		// Handle Date objects or date strings
		if (typeof d === 'string') d = new Date(d);
		if (d instanceof Date && !Number.isNaN(d.getTime())) {
			const pad = (n) => n.toString().padStart(2, '0');
			return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
				d.getHours()
			)}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
		}
		return undefined;
	};

	const handleSubmit = async (values, { setSubmitting: setFormSubmitting }) => {
		setSubmitting(true);
		setApiError('');
		try {
			const formattedDate = formatAppointmentDate(values.appointmentDate);
			const selectedManager = cityManagerOptions.find(
				(opt) => String(opt.value) === String(values.cityManager)
			);
			const cityManagerName = selectedManager ? selectedManager.label : '';
			const payload = {
				create_type: 'business_unit',
				data: {
					name: values.buName,
					code: values.buCode,
					city_manager_id: values.cityManager,
					city_manager: cityManagerName,
					appointed_on: formattedDate,
				},
			};
			const response = await dispatch(addUser({ data: payload }));
			if (response?.data) {
				message.success('Business Unit created successfully');
				onCreate(response.data);
				return 'success';
			}
			if (response?.error?.message) {
				setApiError(response.error.message);
			} else if (response?.error?.message?.code) {
				setApiError(response.error.message.code);
			}
		} catch (error) {
			console.log('Error creating business unit:', error);
		} finally {
			setSubmitting(false);
			setFormSubmitting(false);
		}
		return null;
	};

	const initialValues = useMemo(
		() => ({
			buName: '',
			buCode: '',
			cityManager: selectedCityManager,
			appointmentDate: undefined,
			city: undefined,
		}),
		[buNameOptions, selectedCityManager]
	);

	return (
		<StyledModal
			open={open}
			title="Create New"
			onCancel={() => {
				if (resetFormRef.current) resetFormRef.current();
				onCancel();
			}}
			centered
			maskClosable={false}
			width={883}
			bodyStyle={{ padding: 0 }}
			footer={null}
			closeIcon={
				<CloseOutlined
					onClick={(e) => {
						e.stopPropagation();
						if (resetFormRef.current) resetFormRef.current();
						onCancel();
					}}
					type="button"
					style={{ fontSize: 20, color: '#000' }}
				/>
			}
		>
			<Formik
				initialValues={initialValues}
				enableReinitialize
				onSubmit={async (values, formikHelpers) => {
					await handleSubmit(values, formikHelpers);
				}}
				validate={(values) => {
					const errors = {};
					if (!values.buName && !values.city) errors.buName = 'Required';
					if (!values.buCode) errors.buCode = 'Required';
					if (!values.cityManager) errors.cityManager = 'Required';
					if (!values.appointmentDate) errors.appointmentDate = 'Required';
					return errors;
				}}
			>
				{({ setFieldValue, values, errors, submitCount, resetForm }) => {
					resetFormRef.current = resetForm;
					return (
						<Form id="create-bu-form">
							<ModalContent>
								<FormSection>
									<Row gutter={24} style={{ width: '100%' }}>
										<Col lg={12}>
											<FormInput name="buName" label="BU Name *">
												<CustomInput
													name="buName"
													placeholder="Enter BU Name"
													value={values.buName}
													onChange={(e) =>
														setFieldValue('buName', e.target.value)
													}
												/>
											</FormInput>
										</Col>
										<Col lg={12}>
											<FormInput name="buCode" label="BU Code *">
												<CustomInput
													name="buCode"
													placeholder="Enter BU Code"
													value={values.buCode}
													maxLength={3}
													onChange={(e) => {
														const raw = e.target.value;
														const upper = raw
															.replace(/[^a-zA-Z]/g, '')
															.toUpperCase();
														setFieldValue('buCode', upper);
													}}
												/>
											</FormInput>
										</Col>
									</Row>
									<Row gutter={24} style={{ width: '100%' }}>
										<Col lg={12}>
											<FormInput name="cityManager" label="City Manager *">
												<Select
													name="cityManager"
													placeholder="Select"
													options={cityManagerOptions}
													value={values.cityManager}
													onChange={(val) =>
														setFieldValue('cityManager', val)
													}
													showSearch
													filterOption={(input, option) =>
														option.label
															.toLowerCase()
															.indexOf(input.toLowerCase()) >= 0
													}
													loading={loading}
												/>
											</FormInput>
										</Col>
										<Col lg={12}>
											<FormInput
												name="appointmentDate"
												label="Appointment Date *"
											>
												<DatePicker
													name="appointmentDate"
													format="DD-MM-YYYY"
													style={{ width: '100%' }}
													value={values.appointmentDate}
													onChange={(value) =>
														setFieldValue('appointmentDate', value)
													}
													disabledDate={(current) =>
														current && current < moment().startOf('day')
													}
												/>
											</FormInput>
										</Col>
									</Row>
								</FormSection>
								{submitCount > 0 && Object.keys(errors).length > 0 && (
									<div style={{ color: 'red', marginBottom: 8 }}>
										Please fill all required fields.
									</div>
								)}
								{apiError && (
									<div style={{ color: 'red', marginBottom: 8 }}>{apiError}</div>
								)}
								<FormButtonRow>
									<CancelButtonWrapper
										onClick={() => {
											if (resetFormRef.current) resetFormRef.current();
											onCancel();
										}}
										type="button"
									>
										Cancel
									</CancelButtonWrapper>
									<CreateButtonWrapper
										form="create-bu-form"
										type="submit"
										loading={submitting}
										disabled={
											submitting ||
											!values.buCode ||
											!(values.buName || values.city) ||
											!values.cityManager ||
											!values.appointmentDate
										}
										onClick={async () => {
											if (
												!submitting &&
												!(
													!values.buCode ||
													!(values.buName || values.city) ||
													!values.cityManager ||
													!values.appointmentDate
												)
											) {
												const result = await handleSubmit(values, {
													setSubmitting,
													setFormSubmitting: () => {},
													resetForm,
												});
												if (result === 'success') {
													resetForm();
												}
											}
										}}
									>
										{submitting ? 'Creating...' : 'Create'}
									</CreateButtonWrapper>
								</FormButtonRow>
							</ModalContent>
						</Form>
					);
				}}
			</Formik>
		</StyledModal>
	);
}

CreateBusinessUnitModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	onCreate: PropTypes.func.isRequired,
};
